import { useEffect, useMemo, useState, useCallback } from "react";
import { TopBar } from "@/components/PageShell";
import { Link } from "react-router-dom";
import {
  Search, ArrowLeft, Copy, ExternalLink, FileText, BookOpen,
  Clock, CheckCircle2, AlertTriangle, Loader2, Download, Inbox,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const OUTCOMES = [["","Todos"],["PROCEDENTE","Procedente"],["PARCIALMENTE_PROCEDENTE","Parcial"],["IMPROCEDENTE","Improcedente"],["EXTINTO_SEM_MERITO","Extinto"]];
const APPEAL = [["","Todos"],["PROVIDO","Provido"],["PARCIALMENTE_PROVIDO","Parcial"],["DESPROVIDO","Desprovido"],["NAO_CONHECIDO","Não conhecido"]];

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
const copyText = async (t) => { try { await navigator.clipboard.writeText(t); toast.success("Copiado!"); } catch { toast.error("Falha"); } };
const buildCitation = (it) =>
  `${it.court} · ${it.process_number || "s/n"} · ${it.chamber || ""} · ${it.rapporteur ? `Rel. ${it.rapporteur} · ` : ""}${fmtDate(it.judgment_date)}`;

// Estados do pedido JUDIT → UI. Classes explícitas para Tailwind purge.
const REQUEST_STATE_UI = {
  pending_judit_integration: { label: "Solicitado · aguardando fila", cls: "bg-amber-50 text-amber-700", icon: Clock },
  queued:    { label: "Na fila JUDIT",         cls: "bg-amber-50 text-amber-700",     icon: Clock },
  retry:     { label: "Reprocessando…",         cls: "bg-amber-50 text-amber-700",     icon: Loader2 },
  delivered: { label: "Documento disponível",   cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  failed:    { label: "Falha na entrega",       cls: "bg-rose-50 text-rose-700",       icon: AlertTriangle },
};

export default function Jurisprudencia() {
  const [q, setQ] = useState("");
  const [court, setCourt] = useState("");
  const [merits, setMerits] = useState("");
  const [appeal, setAppeal] = useState("");
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [requestingId, setRequestingId] = useState(null);

  // Índice por source_identifier para rendering em O(1)
  const requestBySource = useMemo(() => {
    const m = {};
    for (const r of myRequests) m[r.source_identifier] = r;
    return m;
  }, [myRequests]);

  const hasPending = useMemo(
    () => myRequests.some(r => ["pending_judit_integration","queued","retry"].includes(r.status)),
    [myRequests],
  );

  const refreshRequests = useCallback(async () => {
    try {
      const r = await api.get("/jurisprudence/doc-requests/mine");
      setMyRequests(r.data.items || []);
    } catch {/* silencioso: painel opcional */}
  }, []);

  useEffect(() => { refreshRequests(); }, [refreshRequests]);

  // Polling enquanto houver pedido pendente — a cada 30s
  useEffect(() => {
    if (!hasPending) return;
    const id = setInterval(refreshRequests, 30_000);
    return () => clearInterval(id);
  }, [hasPending, refreshRequests]);

  const buscar = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (court) params.set("court", court);
      if (merits) params.set("final_merits_outcome", merits);
      if (appeal) params.set("appeal_outcome", appeal);
      params.set("per_page", "20");
      const r = await api.get(`/jurisprudence/search?${params.toString()}`);
      setItems(r.data.items || []); setTotal(r.data.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha na busca");
    } finally { setLoading(false); }
  };

  const requestDoc = async (it) => {
    const sid = it.source_identifier;
    setRequestingId(sid);
    try {
      const r = await api.post(`/jurisprudence/${encodeURIComponent(sid)}/request-document`);
      if (r.data?.already_requested) {
        toast.info("Este documento já foi solicitado — acompanhando na fila.");
      } else {
        toast.success("Solicitação registrada — o Hermes buscará o documento na próxima janela.");
      }
      await refreshRequests();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao solicitar");
    } finally { setRequestingId(null); }
  };

  const downloadDoc = (req) => {
    // O worker devolve o campo `document` (payload JUDIT). Suportamos url direta.
    const doc = req?.document || {};
    const url = doc.url || doc.download_url || doc.pdf_url;
    if (url) { window.open(url, "_blank", "noopener"); return; }
    // Sem URL direta → mostra o payload textual num toast pra transparência
    toast.info("Documento entregue. Contate o suporte para exportar o arquivo bruto.");
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="jurisprudencia-page">
      <TopBar/>
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-3">
        <ArrowLeft className="w-3.5 h-3.5"/> Voltar
      </Link>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center" style={{ background: "linear-gradient(135deg,#0B1B2F,#071B34)" }}>
          <BookOpen className="w-5 h-5" style={{ color: "#F0D37A" }}/>
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Hermes · Banco Nacional</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Pesquisa Jurisprudencial</h1>
        </div>
        <Link
          to="#meus-pedidos"
          onClick={(e) => { e.preventDefault(); document.getElementById("meus-pedidos")?.scrollIntoView({ behavior: "smooth" }); }}
          data-testid="link-meus-pedidos"
          className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
        >
          <Inbox className="w-4 h-4"/>
          Meus pedidos <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-slate-200 text-slate-700 font-semibold">{myRequests.length}</span>
        </Link>
      </div>

      <form onSubmit={buscar} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <input value={q} onChange={e=>setQ(e.target.value)} data-testid="input-jurisprudence-q"
              placeholder="ex: dano moral negativação indevida"
              className="w-full h-12 pl-10 pr-3 rounded-xl border border-slate-200 text-sm"/>
          </div>
          <button type="submit" disabled={loading} data-testid="btn-jurisprudence-search"
            className="h-12 px-6 rounded-xl font-semibold shadow-sm"
            style={{ background: "linear-gradient(180deg,#F5DE8C,#D4AF37)", color: "#0B1B2F" }}>
            {loading ? "Buscando…" : "Buscar"}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
          <select value={court} onChange={e=>setCourt(e.target.value)} data-testid="filter-court" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
            <option value="">Todos tribunais</option>
            <option value="STJ">STJ</option>
            <option value="STF">STF</option>
            <option value="TST">TST</option>
          </select>
          <select value={merits} onChange={e=>setMerits(e.target.value)} data-testid="filter-merits" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
            {OUTCOMES.map(([c,n])=><option key={c} value={c}>{`Resultado: ${n}`}</option>)}
          </select>
          <select value={appeal} onChange={e=>setAppeal(e.target.value)} data-testid="filter-appeal" className="h-10 px-3 rounded-lg border border-slate-200 text-sm">
            {APPEAL.map(([c,n])=><option key={c} value={c}>{`Recurso: ${n}`}</option>)}
          </select>
        </div>
      </form>

      {items === null ? (
        <div className="text-center text-slate-400 text-sm p-12">Digite um termo e clique em Buscar.</div>
      ) : items.length === 0 ? (
        <div className="text-center text-slate-400 text-sm p-12" data-testid="jurisprudence-empty">
          Nenhum julgado encontrado. Tente termos mais amplos ou remova filtros.
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-3" data-testid="jurisprudence-count">{total} julgado{total===1?"":"s"} encontrado{total===1?"":"s"}</div>
          <div className="space-y-3">
            {items.map((it, i) => {
              const req = requestBySource[it.source_identifier];
              const state = req && REQUEST_STATE_UI[req.status];
              const StateIcon = state?.icon;
              const isRequesting = requestingId === it.source_identifier;
              return (
                <div key={it.source_identifier || i} data-testid={`jurisprudence-item-${i}`}
                     className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition">
                  <div className="flex flex-wrap items-baseline gap-2 mb-2">
                    <span className="font-heading text-base font-semibold text-slate-900">{it.court}</span>
                    <span className="text-xs text-slate-500">· {it.process_number || "s/n"}</span>
                    {it.chamber && <span className="text-xs text-slate-500">· {it.chamber}</span>}
                    <div className="flex-1"/>
                    <span className="text-[11px] text-slate-500">{fmtDate(it.judgment_date)}</span>
                  </div>
                  {it.rapporteur && <div className="text-xs text-slate-600 mb-2">Rel. <strong>{it.rapporteur}</strong></div>}
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {it.final_merits_outcome && it.final_merits_outcome !== "NAO_IDENTIFICADO" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{it.final_merits_outcome.replace(/_/g," ").toLowerCase()}</span>
                    )}
                    {it.appeal_outcome && it.appeal_outcome !== "NAO_IDENTIFICADO" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">recurso {it.appeal_outcome.replace(/_/g," ").toLowerCase()}</span>
                    )}
                    {state && (
                      <span data-testid={`request-badge-${i}`}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${state.cls}`}>
                        {StateIcon && <StateIcon className={`w-3 h-3 ${req.status === "retry" ? "animate-spin" : ""}`}/>}
                        {state.label}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed line-clamp-4">{it.headnote}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button onClick={()=>copyText(it.headnote || "")} data-testid={`btn-copy-ementa-${i}`}
                      className="text-xs h-8 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5"><Copy className="w-3 h-3"/> Copiar ementa</button>
                    <button onClick={()=>copyText(buildCitation(it))} data-testid={`btn-copy-cite-${i}`}
                      className="text-xs h-8 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5"><Copy className="w-3 h-3"/> Copiar citação</button>
                    {it.official_url && (
                      <a href={it.official_url} target="_blank" rel="noreferrer" data-testid={`btn-source-${i}`}
                        className="text-xs h-8 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1.5"><ExternalLink className="w-3 h-3"/> Fonte oficial</a>
                    )}
                    {req?.status === "delivered" ? (
                      <button onClick={()=>downloadDoc(req)} data-testid={`btn-download-doc-${i}`}
                        className="text-xs h-8 px-3 rounded-lg font-semibold inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Download className="w-3 h-3"/> Baixar documento
                      </button>
                    ) : (
                      <button onClick={()=>requestDoc(it)} disabled={isRequesting || (req && req.status !== "failed")}
                        data-testid={`btn-request-doc-${i}`}
                        className="text-xs h-8 px-3 rounded-lg font-semibold inline-flex items-center gap-1.5 disabled:opacity-70"
                        style={{ background: "#0B1B2F", color: "#F0D37A" }}>
                        {isRequesting ? <Loader2 className="w-3 h-3 animate-spin"/> : <FileText className="w-3 h-3"/>}
                        {req ? (req.status === "failed" ? "Solicitar novamente" : "Aguardando fila") : "Solicitar documento"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Painel · Meus pedidos JUDIT */}
      {myRequests.length > 0 && (
        <section id="meus-pedidos" className="mt-10 pt-8 border-t border-slate-200" data-testid="section-meus-pedidos">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Fila JUDIT · Sob demanda</div>
              <h2 className="font-heading text-xl font-semibold text-slate-900">Meus pedidos de documento</h2>
            </div>
            <button onClick={refreshRequests} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    data-testid="btn-refresh-my-requests">
              <Loader2 className={`w-3 h-3 ${false ? "animate-spin" : ""}`}/> Atualizar
            </button>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Documento</th>
                  <th className="text-left px-4 py-2">Tribunal</th>
                  <th className="text-left px-4 py-2">Solicitado em</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {myRequests.map((r, idx) => {
                  const state = REQUEST_STATE_UI[r.status] || REQUEST_STATE_UI.queued;
                  const StateIcon = state.icon;
                  return (
                    <tr key={r.source_identifier} className="border-t border-slate-100"
                        data-testid={`my-request-row-${idx}`}>
                      <td className="px-4 py-3 font-mono text-[12px] text-slate-700">{r.source_identifier}</td>
                      <td className="px-4 py-3 text-slate-600">{r.court || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${state.cls}`}>
                          <StateIcon className={`w-3 h-3 ${r.status === "retry" ? "animate-spin" : ""}`}/>
                          {state.label}
                        </span>
                        {r.last_error && (
                          <div className="text-[10px] text-slate-400 mt-0.5 max-w-xs truncate" title={r.last_error}>
                            {r.last_error}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.status === "delivered" ? (
                          <button onClick={()=>downloadDoc(r)} data-testid={`btn-download-row-${idx}`}
                            className="text-xs px-3 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1">
                            <Download className="w-3 h-3"/> Baixar
                          </button>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-2">
            <Clock className="w-3 h-3 mt-0.5 shrink-0"/>
            A fila JUDIT é processada automaticamente a cada 15 minutos. Se a fonte oficial estiver temporariamente indisponível, o Hermes reprocessa até 8 vezes antes de marcar como falha — sem inventar resultado.
          </p>
        </section>
      )}
      </div>
    </div>
  );
}
